import {
    Button,
    Form,
    Input,
    InputNumber,
    Select,
    Switch,
    Tag,
} from "antd";
import React, { useState, useMemo, useEffect } from "react";
import styled from "styled-components";
import configFile from 'config.json';
import axios from 'utils/axios';
import { NotificationManager } from 'react-notifications';

const { TextArea } = Input;

const TAG_API_BASE = `${configFile.PROD_SERVER_URL}/tag`;
const API_BASE = `${configFile.PROD_SERVER_URL}/menu2`;

const formatDateTime = (value) => {
    if (!value) return "-";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "-";
    return date.toLocaleString("en-US", {
        year: "numeric",
        month: "short",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
    });
};

const formatUser = (value) => {
    if (!value) return "-";
    if (typeof value === "string" || typeof value === "number") return String(value);
    if (typeof value === "object") {
        return (
            value.fullName ||
            value.name ||
            value.username ||
            value.email ||
            value.id ||
            "-"
        );
    }
    return "-";
};

const DetailsTab = ({
    menuId,
    form,
    categoryOptions,
    instance,
    onTagAssigned,
    onTagUnassigned,
    onMenuCreated,
}) => {
    const [tags, setTags] = useState([]);
    const colorByValue = useMemo(
        () => new Map(tags.map((o) => [o.slug, o.color])),
        [tags]
    );
    const tagRender = (props) => {
        const { label, closable, onClose } = props;
        const onPreventMouseDown = (event) => {
            event.preventDefault();
            event.stopPropagation();
        };
        return (
            <Tag
                color={colorByValue.get(label) ?? "#d9d9d9"}
                onMouseDown={onPreventMouseDown}
                closable={closable}
                onClose={onClose}
                style={{ marginInlineEnd: 4 }}
            >
                {label}
            </Tag>
        );
    };
    const fetchTags = async () => {
        try {
            const { data: result } = await axios.post(`${TAG_API_BASE}/getAllTags`);
            setTags(result.data);
        } catch (err) {
            NotificationManager.error(
                err?.response?.data?.errorMessage || 'Сервертэй холбогдоход алдаа гарлаа',
                "Error",
                5000
            );
        }
    };

    const assignTag = async (tagId, menuId) => {
        try {
            await axios.post(`${configFile.PROD_SERVER_URL}/menu2/manageMenu/assignTagMenu`, {
                menuId,
                tagId
            });
            return true;
        } catch (err) {
            NotificationManager.error(
                err?.response?.data?.errorMessage || 'Сервертэй холбогдоход алдаа гарлаа',
                "Error",
                5000
            );
            return false;
        }
    };
    const unassignTag = async (tagId, menuId) => {
        try {
            await axios.post(`${configFile.PROD_SERVER_URL}/menu2/manageMenu/unassignTagMenu`,
                {
                    menuId,
                    tagId
                }
            );
            return true;
        } catch (err) {
            NotificationManager.error(
                err?.response?.data?.errorMessage || 'Сервертэй холбогдоход алдаа гарлаа',
                "Error",
                5000
            );
            return false;
        }
    };
    const getCurrentTagIds = () =>
        (instance?.tags || [])
            .map((tag) => tag?.id ?? tag)
            .filter((tag) => tag !== null && typeof tag !== "undefined");

    const resolveTagPayload = (tagId, option) => {
        const selectedTag =
            tags.find((single) => String(single.id) === String(tagId)) ||
            (!Array.isArray(option) ? option : null);
        return {
            id: selectedTag?.id ?? selectedTag?.value ?? tagId,
            slug: selectedTag?.slug ?? selectedTag?.label ?? String(tagId),
            color: selectedTag?.color,
        };
    };

    const handleTagSelect = async (tagId, option) => {
        if (menuId === null || typeof menuId === "undefined") {
            return;
        }
        const isSuccess = await assignTag(tagId, menuId);
        if (!isSuccess) {
            form.setFieldsValue({ tags: getCurrentTagIds() });
            return;
        }
        onTagAssigned?.(menuId, resolveTagPayload(tagId, option));
    };

    const handleTagDeselect = async (tagId) => {
        if (menuId === null || typeof menuId === "undefined") {
            return;
        }
        const isSuccess = await unassignTag(tagId, menuId);
        if (!isSuccess) {
            form.setFieldsValue({ tags: getCurrentTagIds() });
            return;
        }
        onTagUnassigned?.(menuId, tagId);
    };

    const handleSaveDetails = async () => {
        if (!instance?.id) {
            NotificationManager.error("Бүтээгдэхүүн сонгоно уу.");
            return;
        }
        try {
            const values = await form.validateFields();
            const payload = {
                name: values.name,
                majorGroupId: values.majorGroupId,
                defaultPrice: values.defaultPrice,
                description: values.description,
                type: values.type,
                isActive:
                    typeof values.isActive === "boolean"
                        ? values.isActive
                        : typeof instance?.isActive === "boolean"
                            ? instance.isActive
                            : instance?.status === "active"
            };
            if (instance?.isDraft) {
                const response = await axios.post(`${API_BASE}/manageMenu/createMenu`, payload);
                onMenuCreated?.(instance.id, response?.data?.data);
            } else {
                await axios.post(`${API_BASE}/manageMenu/updateMenu`, {
                    ...payload,
                    id: instance.id,
                });
            }
            NotificationManager.success('Амжилттай');
        } catch (err) {
            NotificationManager.error(err?.message || 'Амжилтгүй')

        }
    }
    useEffect(() => {
        fetchTags();
    }, []);
    return (
        <Form layout="vertical" form={form}>
            <FormRow>
                <Form.Item
                    label="Нэр"
                    name="name"
                    rules={[{ required: true, message: "Нэр оруулна уу" }]}
                >
                    <Input placeholder="Маханд дурлагсад" />
                </Form.Item>
                <Form.Item
                    label="Ангилал"
                    name="majorGroupId"
                    rules={[{ required: true, message: "Ангилал сонгоно уу" }]}
                >
                    <Select options={categoryOptions} />
                </Form.Item>
            </FormRow>
            <FormRow>
                <Form.Item label="Default Price" name="defaultPrice">
                    <InputNumber placeholder="0" style={{ width: "100%" }} />
                </Form.Item>
                <Form.Item label="Төрөл" name="type">
                    <Select
                        placeholder="type"
                        options={
                            [
                                {
                                    value: 'pizza',
                                    label: 'pizza'
                                },
                                {
                                    value: 'combo',
                                    label: 'combo'
                                },
                                {
                                    value: 'single',
                                    label: 'single'
                                }
                            ]
                        }
                    />
                </Form.Item>
                <Form.Item
                    label="Идэвхтэй"
                    name="isActive"
                    valuePropName="checked"
                    initialValue={true}
                >
                    <Switch />
                </Form.Item>
            </FormRow>
            <FormRow>
                <Form.Item label="Tags" name="tags">
                    <Select
                        mode="multiple"
                        showSearch
                        tagRender={tagRender}
                        optionFilterProp="label"
                        placeholder='Tag сонгоно уу'
                        onSelect={handleTagSelect}
                        onDeselect={handleTagDeselect}
                        options={tags.map((single) => ({
                            label: single.slug,
                            value: single.id,
                            color: single.color,
                        }))}
                    />
                </Form.Item>
            </FormRow>

            <Form.Item label="Тайлбар" name="description">
                <TextArea rows={4} />
            </Form.Item>
            <AuditInfo>
                <AuditTitle>Audit Info</AuditTitle>
                <AuditGrid>
                    <AuditItem>
                        <AuditLabel>Үүссэн огноо</AuditLabel>
                        <AuditValue>{formatDateTime(instance?.createdAt)}</AuditValue>
                    </AuditItem>
                    <AuditItem>
                        <AuditLabel>Өөрчлөлт хийсэн огноо</AuditLabel>
                        <AuditValue>{formatDateTime(instance?.updatedAt)}</AuditValue>
                    </AuditItem>
                    <AuditItem>
                        <AuditLabel>Өөрчлөлт хийсэн хэрэглэгч</AuditLabel>
                        <AuditValue>{formatUser(instance?.updatedBy)}</AuditValue>
                    </AuditItem>
                </AuditGrid>
            </AuditInfo>
            <Actions>
                <Button type="primary" onClick={handleSaveDetails}>
                    Хадгалах
                </Button>
            </Actions>
        </Form>
    )
};


export default DetailsTab


const FormRow = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
`;

const Actions = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const AuditInfo = styled.div`
  margin: 8px 0 16px;
  padding: 12px;
  border: 1px solid #f0f0f0;
  border-radius: 10px;
  background: #fafafa;
`;

const AuditTitle = styled.div`
  margin-bottom: 10px;
  font-size: 13px;
  font-weight: 600;
  color: #595959;
`;

const AuditGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 10px 14px;
`;

const AuditItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const AuditLabel = styled.span`
  font-size: 12px;
  color: #8c8c8c;
`;

const AuditValue = styled.span`
  font-size: 13px;
  color: #262626;
  word-break: break-word;
`;
